import { Component, OnInit, Input, OnChanges, ViewChild } from '@angular/core';
import { formatPercent } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyService, SearchService, TopicService } from 'services/index';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { get, eq, isEmpty, includes, find } from 'lodash';
import * as actions from 'redux/actions';
import { Notification, FacetSearch, Search } from 'app/models';
import { Paginator } from 'primeng/paginator';
import { MAP_URL } from 'app/pages/topics/constants';
import { LongTermChart } from 'app/interfaces';
import {
  Content,
  RECENT_SORT,
  RELEVANT_SORT,
  STAR_ICON,
  STAR_ICON_FILL,
} from 'app/constants';

@Component({
  selector: 'facet-company-content',
  templateUrl: './facet-company-content.component.html',
  styleUrls: ['./facet-company-content.component.scss'],
})
export class FacetCompanyContentComponent implements OnInit, OnChanges {
  @Input() contentData: any;
  public searchBody: Search;
  public companyContentPage: number;
  public companyContentTotalPages: number;
  public companyContentPageSize: number;
  public companyContentTotal: number;
  public currentPage: number;
  public topicId: number;
  public isTopicSaved: boolean;
  public starToggle: string;
  public sortSelected: string;
  public loading: boolean;
  public isSavingCompany: boolean;
  public savedCompany: any;
  public categories: any[];
  public readonly shortcutLabels = ['Primary', 'Secondary'];

  public company: any;
  public contentItems: any[] = [];
  public relatedTopics: any[];
  public relatedCompanies: any[];
  public currentProject: any;
  public relatedProducts: any[];
  public companiesList: any[];
  public topicsList: any[];
  public longTermChart: LongTermChart;
  public coveragePastYearText: string;
  private longHistoryCompanyId: number;

  @ViewChild('pp', { static: false }) paginator: Paginator;
  public parentName: Content;

  constructor(
    private companyService: CompanyService,
    private searchService: SearchService,
    private topicService: TopicService,
    private store: Store<AppState>,

    private router: Router,
    private route: ActivatedRoute
  ) {
    this.parentName = Content.Company;
    this.isTopicSaved = false;
    this.loading = false;
    this.isSavingCompany = false;
    this.savedCompany = null;
    this.categories = [];
    this.relatedCompanies = null;
    this.relatedTopics = null;
    this.relatedProducts = null;
    this.coveragePastYearText = null;
    this.longTermChart = null;
    this.longHistoryCompanyId = null;
    this.starToggle = STAR_ICON;
  }

  ngOnInit() {
    this.companyContentPage = 1;
    this.currentPage = 0;
    this.store.select('topic').subscribe(({ topic }) => {
      this.topicId = get(topic, 'id', null);
    });
    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody) {
        this.searchBody = searchBody;
      }
    });
    this.store.select('companyDialog').subscribe(({ sort, error }) => {
      this.sortSelected = sort;
      if (error) {
        this.store.dispatch(
          actions.showNotification({
            notify: new Notification('Company not found', 'error', 'tl'),
          })
        );
      }
    });
    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.currentProject = projectSelected;
      }
    });
    this.store.select('topics').subscribe(({ topics }) => {
      this.topicsList = topics;
      if (this.company) {
        this.getIsTopicSaved();
      }
    });

    this.store.select('category').subscribe(({ categories }) => {
      this.categories = categories || [];
    });

    this.store.select('companies').subscribe(({ companies }) => {
      this.companiesList = companies;
    });

    this.store.select('offCanvas').subscribe(({ isOpen }) => {
      if (!isOpen) {
        if (this.currentPage > 0) {
          this.paginator.changePage(0);
        }
      }
    });
  }

  ngOnChanges(changes: any) {
    if (!changes.contentData || !changes.contentData.currentValue) {
      return;
    }
    const { company, contentItems, facetSearch } =
      changes.contentData.currentValue;
    this.company = {
      ...company,
      contact: {},
      picture: this.companyService.getCompanyPicture(company.id),
    };
    this.searchBody = facetSearch.searchBody;
    // this.keywords = this.keywords || company.name;
    this.getRelatedInformation(company.id);
    this.companyContentTotalPages = contentItems.totalPages;
    this.companyContentTotal = contentItems.totalElements;
    this.contentItems = contentItems.content;
    this.companyContentPageSize = facetSearch.size;
    this.currentPage = facetSearch.page;
    this.getIsTopicSaved();
  }

  private setCoverageSection(stats) {
    if (stats) {
      this.coveragePastYearText = `${stats?.newsAuthors.toLocaleString()} authors, ${stats?.newsItems.toLocaleString()} articles (${
        eq(stats?.deltaYearPercent, 0.000001)
          ? '&infin;'
          : formatPercent(Math.abs(stats?.deltaYearPercent), 'en-US')
      } ${
        stats?.deltaYearPercent > 0.000001 ? ' more ' : ' less '
      } than previous year)`;
      this.longTermChart = !isEmpty(stats?.longHistory)
        ? {
            longTermSeries: [
              {
                name: 'longterm-history',
                data: stats.longHistory,
              },
            ],
            longTermColor: [stats?.longTrend > 0 ? 'lightgray' : 'lightgray'],
          }
        : null;
    } else {
      this.longTermChart = null;
      this.coveragePastYearText = null;
    }
  }

  private getRelatedInformation(id) {
    this.topicService.getTopicFull(id).subscribe(
      (response) => {
        if (!eq(this.company?.id, id)) {
          return;
        }
        const { stats } = response;
        const { related } = response;
        if (related) {
          this.relatedCompanies = related?.companies
            ? related.companies.content
            : null;
          this.relatedTopics = related?.topics ? related.topics.content : null;
          this.relatedProducts = related?.products
            ? related.products.content
            : null;
        } else {
          this.relatedCompanies = null;
          this.relatedTopics = null;
          this.relatedProducts = null;
        }
        this.setCoverageSection(stats);
        this.loadLongTermHistory(id, stats);
      },
      (error) => {
        if (!eq(this.company?.id, id)) {
          return;
        }
        this.relatedCompanies = null;
        this.relatedTopics = null;
        this.relatedProducts = null;
        this.setCoverageSection(null);
      }
    );
  }

  private loadLongTermHistory(id, stats) {
    if (
      !isEmpty(stats?.longHistory) ||
      !id ||
      eq(this.longHistoryCompanyId, id)
    ) {
      return;
    }
    this.longHistoryCompanyId = id;
    this.topicService.getTopicHistory(id).subscribe((history) => {
      if (eq(this.company?.id, id)) {
        this.longTermChart = {
          longTermSeries: [
            {
              name: 'longterm-history',
              data: history?.longHistory,
            },
          ],
          longTermColor: [history?.longTrend > 0 ? 'lightgray' : 'lightgray'],
        };
      }
    });
  }

  public sortChange(e) {
    this.sortSelected = e;
    this.companyContentPage = 1;
    this.currentPage = 0;
    this.resetPaginator();
    // this.getCompanyContent();
  }

  getPage(e: any) {
    this.companyContentPage = e.page + 1;
    this.currentPage = e.page;
    this.getCompanyContent();
  }

  getCompanyContent() {
    const facetSearch = new FacetSearch({
      searchBody: new Search({
        ...this.searchBody,
      }),
      sort: this.sortSelected,
      page: this.currentPage,
      size: this.companyContentPageSize,
    });

    this.searchService.searchContent(facetSearch).subscribe((response) => {
      this.companyContentTotalPages = response.totalPages;
      this.companyContentTotal = response.totalElements;
      this.contentItems = response.content;
      // this.scrollTop();
    });
  }

  handleSaveCompany(company) {
    if (this.isTopicSaved) {
      this.store.dispatch(
        actions.deleteTopicToProject({
          topic: { ...company, userTopicId: this.savedCompany?.userTopicId },
          userProject: company.project,
        })
      );
    } else {
      this.store.dispatch(
        actions.addTopicToProject({
          topic: company,
          userProject: company.project,
        })
      );
    }
    this.isSavingCompany = true;
  }

  handleCompanyLabel(labelName: string) {
    const category = find(
      this.categories,
      (item) => String(item.name).toLowerCase() === labelName.toLowerCase()
    );
    if (!category || !this.savedCompany) {
      this.store.dispatch(
        actions.showNotification({
          notify: new Notification(
            `The ${labelName} label is not available`,
            'error',
            'tl'
          ),
        })
      );
      return;
    }
    this.isSavingCompany = true;
    this.store.dispatch(
      actions.updateTopic({
        topic: {
          ...this.savedCompany,
          categoryId: this.companyLabel === labelName ? null : category.id,
        },
        userTopicId: this.savedCompany.userTopicId,
        userId: null,
        userProject: this.currentProject,
      })
    );
  }

  handleIgnoreCompany(currentlyIgnored: boolean) {
    if (currentlyIgnored) {
      this.handleSaveCompany(this.company);
      return;
    }
    const ignore = find(this.categories, (item) => String(item.name).toLowerCase() === 'ignore');
    if (!ignore) {
      this.store.dispatch(actions.showNotification({
        notify: new Notification('The Ignore label is not available', 'error', 'tl'),
      }));
      return;
    }
    this.isSavingCompany = true;
    this.store.dispatch(actions.addTopicToProject({
      topic: { ...this.company, categoryId: ignore.id },
      userProject: this.currentProject,
    }));
  }

  get isCompanyIgnored(): boolean {
    return this.companyLabel === 'Ignore';
  }

  get companyLabel(): string {
    return (
      this.savedCompany?.categoryName || this.savedCompany?.label?.name || null
    );
  }

  resetPaginator() {
    if (this.paginator) {
      this.paginator.changePage(0);
    }
  }

  onDisplayRelatedTopicContent(topic) {
    const sort = /* this.keywords ? RELEVANT_SORT : */ RECENT_SORT;
    this.store.dispatch(actions.startLoading());
    this.store.dispatch(
      actions.setTopicDialog({
        topicSelected: topic,
        projectSelected: this.currentProject,
        keywords: null,
        sort,
        isSidebarTopic: true,
      })
    );
  }

  onDisplayRelatedCompanyContent(company) {
    const sort = /* this.keywords ? RELEVANT_SORT : */ RECENT_SORT;
    this.store.dispatch(
      actions.setCompanyDialog({
        companyId: company.id,
        projectSelected: this.currentProject,
        keywords: null,
        sort,
      })
    );
  }

  onDisplayRelatedProductContent(product) {
    this.topicService.getTopicBasic(product.id).subscribe((response) => {
      const newProduct = {
        id: product.id,
        name: product.name,
        query: product?.query,
        wikipediaUrl: response.contact?.wikipediaUrl,
        webSiteUrl: response.contact?.webSiteUrl,
        youTubeUrl: response.contact?.youTubeUrl,
        twitterUrl: response.contact?.twitterUrl,
        linkedInUrl: response.contact?.linkedInUrl,
        financial: response?.organizationDetail
          ? this.getYahooFinanceURL(response.organizationDetail?.stockTicker)
          : null,
        picture: response.contact?.imageUrl,
        locationUrl: response.contact?.streetAddress
          ? `${MAP_URL}/${response.contact.streetAddress}`
          : '',
        project: this.currentProject,
      };

      const { name, query } = product;
      const q = query ? query : `"${name}"`;
      const keywords = `(\"${q}\")`;
      const facetSearch = new FacetSearch({
        searchBody: new Search({
          ...this.searchBody,
          keywords,
          topics: [product.id],
        }),
      });

      this.store.dispatch(
        actions.loadProductContent({
          facetSearch,
          product: newProduct,
        })
      );
    });
  }

  public goToExploreRelatedCoverage(topic) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/search/topic/${topic.id}/coverage`])
    );

    window.open(url, '_blank');
  }

  // public goToExploreRelatedTopics(topic) {
  //   const url = this.router.serializeUrl(
  //     this.router.createUrlTree([`/search/topic/${topic}/topics`])
  //   );

  //   window.open(url, '_blank');
  // }

  private getIsTopicSaved() {
    setTimeout(() => {
      this.savedCompany = find(
        this.topicsList,
        (i) => eq(i.id, this.company.id) || eq(i.name, this.company.name)
      );
      this.isTopicSaved = !!this.savedCompany;
      this.starToggle = this.isTopicSaved ? STAR_ICON_FILL : STAR_ICON;
      this.isSavingCompany = false;
    }, 500);
  }

  getYahooFinanceURL(value: any) {
    if (isEmpty(value)) {
      return null;
    } else if (includes(value, 'http')) {
      const q = value.substring(value.lastIndexOf('/') + 1, value.length);
      return `https://finance.yahoo.com/quote/${q}`;
    } else {
      return `https://finance.yahoo.com/quote/${value}`;
    }
  }
}
